import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { MessageResponse } from "@bw/shared/types/generated";

/**
 * Scroll only for a server-confirmed local send. Passive polls intentionally do
 * not set a target, so a reader who is reviewing earlier history keeps their
 * place in the thread.
 */
export function useScrollToConfirmedMessage(
  renderedMessageIds: readonly string[],
): {
  scrollContainerRef: RefObject<HTMLOListElement | null>;
  onMessageSent: (message: Pick<MessageResponse, "message_id">) => void;
} {
  const scrollContainerRef = useRef<HTMLOListElement | null>(null);
  const [targetMessageId, setTargetMessageId] = useState<string | null>(null);

  const onMessageSent = useCallback(
    (message: Pick<MessageResponse, "message_id">): void => {
      setTargetMessageId(message.message_id);
    },
    [],
  );

  useEffect(() => {
    if (!targetMessageId || !renderedMessageIds.includes(targetMessageId)) {
      return;
    }
    const container = scrollContainerRef.current;
    if (!container) return;

    if (typeof container.scrollTo === "function") {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    } else {
      container.scrollTop = container.scrollHeight;
    }
    setTargetMessageId(null);
  }, [renderedMessageIds, targetMessageId]);

  return { scrollContainerRef, onMessageSent };
}
